import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Droplets } from "lucide-react";

const BamniyaWaterfall = () => {
  const navigate = useNavigate();
  const waterfallImage = "/Indore/waterfalls/bamniya0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Bamniya Waterfall",
    "description": "Beautiful waterfall near Indore known for its scenic beauty and natural surroundings",
    "image": waterfallImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bamniya Waterfall Area",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-18:00",
    "priceRange": "₹10-50",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.3",
      "reviewCount": "4200"
    }
  };

  const highlights = [
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Scenic Waterfall",
      description: "Beautiful waterfall with cascading waters and natural beauty"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Photography Spot",
      description: "Perfect location for nature photography and scenic views"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Nature Retreat",
      description: "Peaceful destination for nature lovers and adventure seekers"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Natural Beauty",
      description: "Surrounded by lush greenery and natural landscapes"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "₹10 for adults, ₹5 for children" },
    { label: "Timings", value: "6:00 AM - 6:00 PM (All days)" },
    { label: "Best Time", value: "Monsoon season (July-September)" },
    { label: "Duration", value: "2-3 hours" },
    { label: "Facilities", value: "Parking, Restrooms, Small shops" }
  ];

  const activities = [
    { name: "Photography", description: "Capture stunning natural landscapes and waterfall scenes" },
    { name: "Nature Walk", description: "Explore the surrounding forests and trails" },
    { name: "Picnic", description: "Enjoy a peaceful picnic amidst nature" },
    { name: "Bird Watching", description: "Observe various bird species in their natural habitat" }
  ];

  const nearbyAttractions = [
    { name: "Patalpani Waterfall", distance: "15 km", time: "25 mins" },
    { name: "Tincha Waterfall", distance: "20 km", time: "30 mins" },
    { name: "Choral Dam", distance: "25 km", time: "35 mins" },
    { name: "Ralamandal Wildlife Sanctuary", distance: "30 km", time: "40 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Bamniya Waterfall Indore - Scenic Natural Beauty | Aaoghumen</title>
        <meta name="description" content="Visit Bamniya Waterfall near Indore with Aaoghumen - beautiful waterfall with cascading waters. Complete guide with timings, activities, and natural beauty." />
        <meta name="keywords" content="bamniya waterfall indore, bamniya waterfall timings, indore waterfalls, scenic waterfall, aaoghumen bamniya, nature spots indore" />

        <meta property="og:title" content="Bamniya Waterfall Indore - Natural Beauty | Aaoghumen" />
        <meta property="og:description" content="Experience the scenic beauty of Bamniya Waterfall near Indore, perfect for nature lovers and photographers." />
        <meta property="og:image" content={waterfallImage} />
        <meta name="twitter:image" content={waterfallImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/bamniya-waterfall" />

        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/bamniya-waterfall" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-cyan-600 via-blue-500 to-teal-600 overflow-hidden">
          <img
            src={waterfallImage}
            alt="Bamniya Waterfall Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-cyan-300">Bamniya Waterfall</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Experience the mesmerizing beauty of cascading waters amidst lush greenery at this scenic waterfall.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.3 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 6 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>₹10 Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Bamniya Waterfall</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Bamniya Waterfall is a beautiful natural attraction located near Indore, known for its scenic beauty and peaceful surroundings. The waterfall cascades down from a height, creating a mesmerizing sight especially during the monsoon season when the water flow is at its peak.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Surrounded by lush green forests and natural landscapes, the waterfall offers a perfect retreat for nature lovers, photographers, and those seeking peace away from the city hustle. The area provides excellent opportunities for bird watching and exploring the local flora and fauna.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the natural beauty of Bamniya Waterfall, enjoy various activities, and create unforgettable memories in this serene natural paradise.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Waterfall Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-cyan-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-cyan-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Activities */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Activities & Experiences</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {activities.map((activity, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-cyan-600 mb-2">{activity.name}</h3>
                  <p className="text-gray-600">{activity.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-cyan-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Car:</strong> Well-connected roads from Indore city</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Parking available near the entrance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Distance:</strong> Approximately 25-30 km from city center</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> Local buses available from city bus stand</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Available from all parts of the city</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Bamniya Waterfall Indore" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Natural Beauty</h2>
              <p className="text-xl mb-6">Visit Bamniya Waterfall with expert guidance and enjoy nature's splendor</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Bamniya Waterfall Area, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-cyan-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Waterfall Visit
                </button>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-cyan-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default BamniyaWaterfall;